/**
 * localStorage wrapper for the pre-signup push-permission choice.
 *
 * A signed-out visitor on the native app can be asked for push
 * permission at first launch (Option A follow-up to the signed-in
 * PushPromptModal). Because there's no userId yet, we cannot write
 * to `PushPromptState` on the server. We stash the choice in
 * localStorage, per-venue, so the post-signup register hook can
 * pick it up and:
 *   - silently register the token if the user granted permission,
 *   - or record system_denied so the modal never fires,
 *   - or wait for the normal post-signup prompt if they dismissed.
 *
 * Scoped per-venue: the same device visiting Love Inn and Toka
 * gets independent state so a "no thanks" for one doesn't silence
 * the other.
 *
 * Pure module: no React, no fetch, no Capacitor. Safe to unit-test
 * from root vitest.
 */

export type PreAuthPushResponse = "granted" | "denied" | "asked_dismissed";

export type PreAuthPushState = {
  response: PreAuthPushResponse;
  venueId: string;
  updatedAt: number;
};

const KEY_PREFIX = "assembly.push.preauth.";
// Match the signed-in cool-off (7 days). If a user dismissed pre-signup
// and hasn't come back or signed up in a week, we ask again.
const DISMISS_COOLOFF_MS = 7 * 24 * 60 * 60 * 1000;

function keyFor(venueId: string): string {
  return `${KEY_PREFIX}${venueId}`;
}

/**
 * Read the stored state for this venue. Never throws (localStorage can be
 * disabled by the browser; a private-mode Safari session can raise on
 * writes; a malformed JSON blob can happen after a schema change).
 */
export function readPreAuthPushState(
  venueId: string,
  storage: Storage | null = defaultStorage(),
): PreAuthPushState | null {
  if (!venueId || !storage) return null;
  try {
    const raw = storage.getItem(keyFor(venueId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PreAuthPushState;
    // Sanity-check the shape: a schema change or third-party wipe
    // could leave garbage behind. Treat garbage as "never asked".
    if (!parsed || typeof parsed !== "object") return null;
    if (!isKnownResponse(parsed.response)) return null;
    if (typeof parsed.updatedAt !== "number") return null;
    if (parsed.venueId !== venueId) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persist the user's pre-signup choice. Overwrites any prior state
 * (the newest decision wins). Fail-silent on storage errors.
 */
export function writePreAuthPushState(
  input: { venueId: string; response: PreAuthPushResponse; now?: number },
  storage: Storage | null = defaultStorage(),
): void {
  if (!input.venueId || !storage) return;
  const state: PreAuthPushState = {
    response: input.response,
    venueId: input.venueId,
    updatedAt: input.now ?? Date.now(),
  };
  try {
    storage.setItem(keyFor(input.venueId), JSON.stringify(state));
  } catch {
    /* storage disabled; the welcome prompt just fires again next launch */
  }
}

/**
 * Clear the stored state. Called after the post-signup register hook
 * has successfully translated the preauth choice into a server-side
 * PushPromptState + PushToken; there's no reason to keep the local
 * copy after that (the server is the source of truth for signed-in
 * users).
 */
export function clearPreAuthPushState(
  venueId: string,
  storage: Storage | null = defaultStorage(),
): void {
  if (!venueId || !storage) return;
  try {
    storage.removeItem(keyFor(venueId));
  } catch {
    /* storage disabled */
  }
}

/**
 * Pure decision: should the welcome-push-prompt modal render now?
 * Signed-in check + isNativeApp + venueId presence live at the
 * callsite; this module cares only about the localStorage state.
 *
 * Rules:
 *   - No state stored: show (first-ever ask).
 *   - `granted` or `denied`: never re-show (already resolved; the
 *     post-signup hook will act on it).
 *   - `asked_dismissed`: re-show after 7-day cool-off.
 */
export function shouldShowWelcomePrompt(
  state: PreAuthPushState | null,
  now: number = Date.now(),
): boolean {
  if (!state) return true;
  if (state.response === "granted" || state.response === "denied") return false;
  // asked_dismissed: cool-off
  return now - state.updatedAt >= DISMISS_COOLOFF_MS;
}

function isKnownResponse(x: unknown): x is PreAuthPushResponse {
  return x === "granted" || x === "denied" || x === "asked_dismissed";
}

function defaultStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
